import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Boxes,
  Route,
  ClipboardList,
  MoveVertical,
  CalendarDays,
  FileCheck2,
} from "lucide-react";

const CRITERES = [
  {
    icon: Boxes,
    title: "Le volume à déménager",
    desc: "Le volume de vos biens, exprimé en mètres cubes (m³), est le principal critère de calcul. Plus le volume est important, plus le camion devra être spacieux et plus le temps de chargement, de transport et de déchargement sera conséquent. Ce critère détermine également le nombre de déménageurs nécessaires pour réaliser la prestation dans les meilleures conditions.",
  },
  {
    icon: Route,
    title: "La distance entre les deux adresses",
    desc: "La distance à parcourir entre votre logement actuel et votre nouvelle adresse influence directement le prix du déménagement. Les frais de carburant, les péages, l'usure du véhicule ainsi que le temps de trajet de l'équipe sont intégrés dans le calcul du devis.",
  },
  {
    icon: ClipboardList,
    title: "La formule de déménagement choisie",
    desc: "Le tarif varie également selon la formule sélectionnée. Une formule économique, dans laquelle vous préparez vous-même vos cartons, sera plus abordable. À l'inverse, une formule confort ou clé en main comprend l'emballage de vos effets personnels, la protection des objets fragiles, le démontage et le remontage du mobilier ainsi que l'ensemble des opérations de manutention.",
  },
  {
    icon: MoveVertical,
    title: "L'accessibilité des logements",
    desc: "Les conditions d'accès au logement de départ comme à celui d'arrivée peuvent faire évoluer le prix de la prestation. Un appartement situé en étage sans ascenseur, une cage d'escalier étroite, un accès difficile ou l'impossibilité de stationner le camion à proximité peuvent rallonger le temps d'intervention. Dans certains cas, l'utilisation d'un monte-meubles est également nécessaire.",
  },
  {
    icon: CalendarDays,
    title: "La période du déménagement",
    desc: "La date de votre déménagement a également un impact sur le tarif. Les mois de juin à septembre, les fins de mois et les week-ends correspondent à la haute saison et sont généralement les périodes les plus demandées. En privilégiant un déménagement en semaine ou en basse saison, il est souvent possible de bénéficier de tarifs plus avantageux.",
  },
];

function CritereCard({ c, index }) {
  const [open, setOpen] = useState(false);
  const CIcon = c.icon;
  return (
    <li
      data-testid={`home-prix-critere-${index + 1}`}
      className="rounded-2xl bg-[#f7fafc] border border-[#e6edf3] p-5 sm:p-6 transition-[transform,box-shadow,border-color,background-color,color] hover:border-[#237cb5] hover:shadow-[0_20px_40px_-24px_rgba(35,124,181,0.45)] hover:-translate-y-1"
    >
      <div className="flex items-center gap-3 sm:block">
        <span className="inline-flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl bg-[#eaf3fb] text-[#237cb5] shrink-0">
          <CIcon className="h-5 w-5" />
        </span>
        <h3 className="text-[15px] font-extrabold text-[#0f1d2b] sm:mt-4">{c.title}</h3>
      </div>
      <p
        className={`mt-2 sm:mt-2 text-[13.5px] text-[#3a4a5a] leading-relaxed ${
          open ? "" : "line-clamp-3"
        } sm:line-clamp-none`}
      >
        {c.desc}
      </p>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`home-prix-critere-${index + 1}-toggle`}
        aria-expanded={open}
        className="sm:hidden mt-2 text-[13px] font-semibold text-[#237cb5] underline underline-offset-2"
      >
        {open ? "Réduire" : "Lire la suite"}
      </button>
    </li>
  );
}

export default function PrixDemenagement() {
  return (
    <>
    <section
      id="prix-demenagement"
      data-testid="home-prix-demenagement"
      className="py-12 sm:py-16 bg-white"
      aria-labelledby="home-prix-title"
    >
      <div className="bd-container">
        <div className="max-w-3xl">
          <span className="bd-eyebrow">Tarification transparente</span>
          <h2
            id="home-prix-title"
            className="bd-h2 mt-4 text-[26px] sm:text-[30px] lg:text-[36px]"
          >
            Prix d&apos;un déménagement : les critères qui{" "}
            <span style={{ color: "#237cb5" }}>influencent le tarif</span>
          </h2>
          <p className="mt-6 text-[15px] sm:text-base leading-[1.8] text-[#3a4a5a]">
            Le prix d&apos;un déménagement professionnel{" "}
            <strong className="text-[#0f1d2b] font-semibold">n&apos;est jamais fixe</strong>.
            Chaque projet est unique et nécessite une étude personnalisée afin d&apos;établir un
            devis précis. Plusieurs critères sont pris en compte pour calculer le coût de votre
            déménagement, qu&apos;il s&apos;agisse du volume à transporter, de la distance à
            parcourir ou encore des prestations choisies.
          </p>
          <p className="mt-4 text-[15px] sm:text-base leading-[1.8] text-[#3a4a5a]">
            Pour comprendre le montant indiqué sur votre devis, découvrez les principaux
            éléments qui influencent le tarif de votre déménagement.
          </p>
        </div>

        <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {CRITERES.map((c, i) => (
            <CritereCard key={c.title} c={c} index={i} />
          ))}
        </ul>

        <div
          data-testid="home-prix-devis"
          className="mt-8 rounded-2xl border border-[#e6edf3] bg-[#f7fafc] p-6 sm:p-8"
        >
          <div className="flex items-start gap-4">
            <span className="hidden sm:inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#eaf3fb] text-[#237cb5] shrink-0">
              <FileCheck2 className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-[16px] sm:text-lg font-extrabold text-[#0f1d2b]">
                Un devis de déménagement transparent et personnalisé
              </h3>
              <p className="mt-2 text-[14px] sm:text-[15px] text-[#3a4a5a] leading-relaxed">
                Chaque déménagement possède ses propres contraintes. C&apos;est pourquoi nos
                experts réalisent une étude complète de votre projet afin de vous proposer un{" "}
                <strong className="text-[#0f1d2b] font-semibold">
                  devis de déménagement clair, détaillé et sans surprise
                </strong>
                . Vous bénéficiez ainsi d&apos;un tarif adapté à vos besoins, à votre budget et
                aux caractéristiques de votre logement, avec une prestation réalisée dans les
                meilleures conditions.
              </p>
              <Link
                to="/demande-de-devis"
                data-testid="home-prix-devis-cta"
                className="mt-5 h-11 inline-flex items-center justify-center gap-2 rounded-xl px-5 bg-[#FF7A00] hover:bg-[#E66E00] text-white text-[14px] font-semibold shadow-[0_18px_30px_-12px_rgba(255,122,0,0.55)] transition-colors whitespace-nowrap"
              >
                Demander mon devis gratuit
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      id="prix-demenagement-local"
      data-testid="home-prix-local"
      className="py-12 sm:py-16 bg-[#f7fafc] border-y border-[#e6edf3]"
      aria-labelledby="home-prix-local-title"
    >
      <div className="bd-container">
        <div className="max-w-3xl">
          <span className="bd-eyebrow">Tarifs locaux</span>
          <h2
            id="home-prix-local-title"
            className="bd-h2 mt-4 text-[26px] sm:text-[30px] lg:text-[36px]"
          >
            Combien coûte un{" "}
            <span style={{ color: "#237cb5" }}>déménagement local en Île-de-France</span>
            &nbsp;?
          </h2>
          <p className="mt-6 text-[14px] sm:text-[15px] text-[#3a4a5a] leading-relaxed max-w-3xl">
            Le prix d&apos;un déménagement local en Île-de-France dépend principalement du volume
            à transporter, des conditions d&apos;accès aux logements et des prestations choisies.
            Pour vous donner un premier aperçu, voici quelques{" "}
            <strong className="text-[#0f1d2b] font-semibold">tarifs indicatifs</strong> pour un
            déménagement local.
          </p>
        </div>

          <ul
            data-testid="home-prix-local-list"
            className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl"
          >
            {[
              { volume: "20 m³", desc: "Appartement T1 / T2", price: 600 },
              { volume: "30 m³", desc: "Appartement T2 / T3", price: 800 },
              { volume: "40 m³", desc: "Maison T3 / T4", price: 1100 },
              { volume: "60 m³", desc: "Grande maison T4 / T5", price: 1400 },
            ].map((t, idx) => (
              <li
                key={t.volume}
                data-testid={`home-prix-local-${idx}`}
                className="group flex flex-col rounded-xl border border-[#e6edf3] bg-white p-4 transition-[transform,box-shadow,border-color] hover:border-[#237cb5] hover:shadow-[0_20px_40px_-24px_rgba(35,124,181,0.45)] hover:-translate-y-1"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#eaf3fb] text-[#237cb5] shrink-0">
                    <Boxes className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5b6b7a]">
                      Volume
                    </div>
                    <div className="text-lg font-extrabold text-[#0f1d2b] leading-tight">{t.volume}</div>
                  </div>
                </div>
                <p className="mt-2 text-[12px] text-[#5b6b7a]">{t.desc}</p>
                <div className="mt-3 pt-3 border-t border-dashed border-[#e6edf3]">
                  <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#5b6b7a]">
                    à partir de
                  </span>
                  <div className="mt-0.5 flex items-baseline gap-1">
                    <span className="text-xl font-extrabold text-[#FF7A00] leading-none">
                      {t.price.toLocaleString("fr-FR")}
                    </span>
                    <span className="text-sm font-bold text-[#FF7A00] leading-none">€</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <p className="mt-4 text-[12.5px] text-[#7b8a99] leading-relaxed italic">
            *Tarifs indicatifs : tarifs applicables pour un déménagement en formule économique,
            de rez-de-chaussée à rez-de-chaussée, dans un rayon maximal de 10 km.
          </p>
      </div>
    </section>

    <section
      id="prix-longue-distance"
      data-testid="home-prix-longue-distance"
      className="py-12 sm:py-16 bg-white"
      aria-labelledby="home-prix-ld-title"
    >
      <div className="bd-container">
        <div className="max-w-3xl">
          <span className="bd-eyebrow">Tarifs longue distance</span>
          <h2
            id="home-prix-ld-title"
            className="bd-h2 mt-4 text-[26px] sm:text-[30px] lg:text-[36px]"
          >
            Tarifs d&apos;un{" "}
            <span style={{ color: "#237cb5" }}>déménagement longue distance</span> depuis
            l&apos;Île-de-France
          </h2>
          <p className="mt-6 text-[14px] sm:text-[15px] text-[#3a4a5a] leading-relaxed max-w-3xl">
            Le prix d&apos;un{" "}
            <Link
              to="/demenagement-longue-distance"
              data-testid="home-prix-ld-link"
              className="font-semibold text-[#237cb5] underline underline-offset-2 hover:text-[#1a5d8a] transition-colors"
            >
              déménagement longue distance
            </Link>{" "}
            depuis l&apos;Île-de-France dépend principalement de
            la distance à parcourir, du volume à transporter et des conditions d&apos;accès aux
            logements. Voici quelques exemples de{" "}
            <strong className="text-[#0f1d2b] font-semibold">tarifs indicatifs</strong> pour un
            déménagement de 20 m³.
          </p>
        </div>

          <ul
            data-testid="home-prix-ld-list"
            className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-3xl"
          >
            {[
              { distance: "400 km", price: 2000 },
              { distance: "600 km", price: 2300 },
              { distance: "800 km", price: 2600 },
            ].map((t, idx) => (
              <li
                key={t.distance}
                data-testid={`home-prix-ld-${idx}`}
                className="group flex flex-col rounded-xl border border-[#e6edf3] bg-white p-4 transition-[transform,box-shadow,border-color] hover:border-[#237cb5] hover:shadow-[0_20px_40px_-24px_rgba(35,124,181,0.45)] hover:-translate-y-1"
              >
                <div className="flex items-center justify-between gap-3 sm:justify-start">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#eaf3fb] text-[#237cb5] shrink-0">
                      <Route className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5b6b7a]">
                        Distance
                      </div>
                      <div className="text-lg font-extrabold text-[#0f1d2b] leading-tight">{t.distance}</div>
                    </div>
                  </div>
                  <div className="text-right sm:hidden">
                    <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#5b6b7a]">
                      à partir de
                    </span>
                    <div className="mt-0.5 flex items-baseline justify-end gap-1">
                      <span className="text-xl font-extrabold text-[#FF7A00] leading-none">
                        {t.price.toLocaleString("fr-FR")}
                      </span>
                      <span className="text-sm font-bold text-[#FF7A00] leading-none">€</span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-dashed border-[#e6edf3] hidden sm:block">
                  <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#5b6b7a]">
                    à partir de
                  </span>
                  <div className="mt-0.5 flex items-baseline gap-1">
                    <span className="text-xl font-extrabold text-[#FF7A00] leading-none">
                      {t.price.toLocaleString("fr-FR")}
                    </span>
                    <span className="text-sm font-bold text-[#FF7A00] leading-none">€</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <p className="mt-4 text-[12.5px] text-[#7b8a99] leading-relaxed italic">
            *Tarifs indicatifs : prix applicables pour un déménagement de 20 m³ au départ de
            l&apos;Île-de-France, de rez-de-chaussée à rez-de-chaussée, en formule économique.
          </p>
      </div>
    </section>
    </>
  );
}
